import { uiEffectsEnabled } from "../../../graphics/graphics_quality.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";

export class AdventureTigerBasket
{
  private readonly ctx: CanvasRenderingContext2D;

  private readonly TIGER_W = 340;
  private readonly TIGER_H = 430;
  private readonly TIGER_EYES_W = 103;
  private readonly TIGER_EYES_H = 37;

  private readonly TIGER_HEAD_W = 249;
  private readonly TIGER_HEAD_H = 247;
  private readonly TIGER_HEAD_CENTER_X = 185;
  private readonly TIGER_HEAD_BOTTOM_Y = 105;

  private readonly BALLOON_RED_W = 151;
  private readonly BALLOON_RED_H = 174;
  private readonly BALLOON_PURPLE_W = 151;
  private readonly BALLOON_PURPLE_H = 174;
  private readonly BALLOON_BLUE_W = 164;
  private readonly BALLOON_BLUE_H = 189;

  private readonly BALLOON_CLUSTER_CENTER_X = 170;
  private readonly BALLOON_STRING_RIM_Y = 120;
  private readonly BALLOON_STRING_WIDTH = 3;

  private readonly BALLOONS: { img_id: ImgID, w: number, h: number, dx: number, dy: number }[] = [
    { img_id: ImgID.UI_AdventureBalloonRed, w: this.BALLOON_RED_W, h: this.BALLOON_RED_H, dx: -70, dy: -380 },
    { img_id: ImgID.UI_AdventureBalloonPurple, w: this.BALLOON_PURPLE_W, h: this.BALLOON_PURPLE_H, dx: 10, dy: -410 },
    { img_id: ImgID.UI_AdventureBalloonBlue, w: this.BALLOON_BLUE_W, h: this.BALLOON_BLUE_H, dx: 90, dy: -375 },
    { img_id: ImgID.UI_AdventureBalloonYellow, w: this.BALLOON_RED_W, h: this.BALLOON_RED_H, dx: -70, dy: -320 },
    { img_id: ImgID.UI_AdventureBalloonBlue, w: this.BALLOON_BLUE_W, h: this.BALLOON_BLUE_H, dx: 60, dy: -295 },
    { img_id: ImgID.UI_AdventureBalloonPurple, w: this.BALLOON_PURPLE_W, h: this.BALLOON_PURPLE_H, dx: 10, dy: -250 },
    { img_id: ImgID.UI_AdventureBalloonRed, w: this.BALLOON_RED_W, h: this.BALLOON_RED_H, dx: -75, dy: -205 },
    { img_id: ImgID.UI_AdventureBalloonYellow, w: this.BALLOON_RED_W, h: this.BALLOON_RED_H, dx: 80, dy: -210 },
  ];

  private readonly BALLOON_SWAY_MIN_AMPLITUDE = 3;
  private readonly BALLOON_SWAY_MAX_AMPLITUDE = 7;
  private readonly BALLOON_SWAY_MIN_MS = 1900;
  private readonly BALLOON_SWAY_MAX_MS = 3200;

  private readonly TIGER_BLINK_MIN_GAP_MS = 2200;
  private readonly TIGER_BLINK_MAX_GAP_MS = 5000;
  private readonly TIGER_BLINK_CLOSE_MS = 55;
  private readonly TIGER_BLINK_HOLD_MS = 90;
  private readonly TIGER_BLINK_OPEN_MS = 75;

  private readonly TIGER_HEAD_SWAY_DEG = 3;
  private readonly TIGER_HEAD_SWAY_MS = 6800;

  private readonly ROPE_TILE_W = 20;
  private readonly ROPE_TILE_H = 18;
  private readonly ROPE_TIGER_OVERLAP = 20;
  private readonly ROPE_COIL_OVERLAP = 20;
  private readonly MAX_BOTTOM_MARGIN = 200;
  private readonly FLOOR_STOP_OFFSET_Y = 35;

  private coil: UIImage | null = null;
  private coil_x = 0;
  private coil_center_x = 0;
  private coil_top_content_y = 0;

  private rope_tiles: UIImage[] = [];
  private rope_tile_step = 0;
  private rope_x = 0;
  private tiger_bottom_y = 0;
  private coil_rope_anchor_y = 0;
  private last_layout_scroll_offset = Number.NaN;

  constructor( ctx: CanvasRenderingContext2D )
  {
    this.ctx = ctx;
  }

  computeAnchor( screen_w: number, content_h: number ): void
  {
    const coil_w = LayoutScaler.widthFromDesign( 252 );
    const coil_h = LayoutScaler.heightFromDesign( 168 );

    this.coil_x = screen_w - coil_w - LayoutScaler.xFromDesign( 70 );
    this.coil_top_content_y = content_h - coil_h - LayoutScaler.yFromDesign( 115 );
    this.coil_center_x = this.coil_x + coil_w / 2;
  }

  preferredMarkerY( viewport_h: number ): number
  {
    return viewport_h / 2;
  }

  bottomForMarkerY( marker_y: number, is_exact_floor: boolean ): number
  {
    const floor_offset = is_exact_floor
      ? LayoutScaler.yFromDesign( this.FLOOR_STOP_OFFSET_Y )
      : 0;
    return marker_y
      + LayoutScaler.heightFromDesign( this.TIGER_H ) / 2
      + floor_offset;
  }

  clampBottomY( bottom_y: number, viewport_h: number ): number
  {
    return Math.min(
      bottom_y,
      viewport_h - LayoutScaler.heightFromDesign( this.MAX_BOTTOM_MARGIN )
    );
  }

  build( content: UIElement, screen_w: number, tiger_bottom_y: number ): void
  {
    this.buildRope( content, tiger_bottom_y );
    this.buildCoil( content );
    this.buildTiger( content, screen_w, tiger_bottom_y );
  }

  private buildRope( scroll_container: UIElement, tiger_image_bottom_y: number ): void
  {
    const tile_w = LayoutScaler.widthFromDesign( this.ROPE_TILE_W );
    const tile_h = LayoutScaler.heightFromDesign( this.ROPE_TILE_H );

    this.rope_tile_step = tile_h - 1;
    this.rope_x = this.coil_center_x - LayoutScaler.xFromDesign( 28 );

    this.tiger_bottom_y = tiger_image_bottom_y - LayoutScaler.yFromDesign( this.ROPE_TIGER_OVERLAP );
    this.coil_rope_anchor_y = this.coil_top_content_y + LayoutScaler.yFromDesign( this.ROPE_COIL_OVERLAP );

    const max_span = this.coil_rope_anchor_y - this.tiger_bottom_y;
    const pool_size = Math.max( 0, Math.ceil( max_span / this.rope_tile_step ) ) + 1;

    for ( let i = 0; i < pool_size; i++ )
    {
      const tile_y = this.tiger_bottom_y + i * this.rope_tile_step;
      const tile = new UIImage( this.ctx, this.rope_x, tile_y, tile_w, tile_h, ImgID.UI_AdventureCoilLine );
      this.rope_tiles.push( tile );
      scroll_container.addChild( tile );
    }
  }

  private buildCoil( scroll_container: UIElement ): void
  {
    const coil_w = LayoutScaler.widthFromDesign( 252 );
    const coil_h = LayoutScaler.heightFromDesign( 168 );

    this.coil = new UIImage( this.ctx, this.coil_x, this.coil_top_content_y, coil_w, coil_h, ImgID.UI_AdventureCoil );
    scroll_container.addChild( this.coil );
  }

  private buildTiger( scroll_container: UIElement, screen_w: number, tiger_bottom_y: number ): void
  {
    const tiger_w = LayoutScaler.widthFromDesign( this.TIGER_W );
    const tiger_h = LayoutScaler.heightFromDesign( this.TIGER_H );

    const tiger_x = screen_w - tiger_w - LayoutScaler.xFromDesign( 51.5 );
    const tiger_y = tiger_bottom_y - tiger_h;

    this.buildBalloons( scroll_container, tiger_x, tiger_y );

    const tiger = new UIImage( this.ctx, tiger_x, tiger_y, tiger_w, tiger_h, ImgID.UI_AdventureTiger );
    this.buildTigerHead( tiger );

    scroll_container.addChild( tiger );
  }

  private buildTigerHead( tiger: UIElement ): void
  {
    const w = LayoutScaler.widthFromDesign( this.TIGER_HEAD_W );
    const h = LayoutScaler.heightFromDesign( this.TIGER_HEAD_H );

    const x = LayoutScaler.xFromDesign( this.TIGER_HEAD_CENTER_X ) - w / 2;
    const y = LayoutScaler.yFromDesign( this.TIGER_HEAD_BOTTOM_Y ) - h;

    const head = new UIImage( this.ctx, x, y, w, h, ImgID.UI_AdventureTigerHead );
    tiger.addChild( head );

    const eyes_w = LayoutScaler.widthFromDesign( this.TIGER_EYES_W );
    const eyes_h = LayoutScaler.heightFromDesign( this.TIGER_EYES_H );
    const eyes_x = LayoutScaler.xFromDesign( 71.7 );
    const eyes_y = LayoutScaler.yFromDesign( 113 );

    const eyes = new UIImage( this.ctx, eyes_x, eyes_y, eyes_w, eyes_h, ImgID.UI_TigerEyes, { rotation_angle: 16 } );
    eyes.setOpacityImmediate( 0 );
    head.addChild( eyes );

    this.scheduleBlink( eyes );
    this.startHeadSway( head );
  }

  private scheduleBlink( eyes: UIImage ): void
  {
    const gap = this.TIGER_BLINK_MIN_GAP_MS + Math.random() * ( this.TIGER_BLINK_MAX_GAP_MS - this.TIGER_BLINK_MIN_GAP_MS );
    const close_start = performance.now() + gap;

    const close = new TransformOpacity( eyes, true, 0, 1, close_start, this.TIGER_BLINK_CLOSE_MS, { easing: Easing.EaseOutQuad } );
    eyes.addTransform( close );

    const open_start = close_start + this.TIGER_BLINK_CLOSE_MS + this.TIGER_BLINK_HOLD_MS;
    const open = new TransformOpacity( eyes, true, 1, 0, open_start, this.TIGER_BLINK_OPEN_MS, { easing: Easing.EaseInQuad } );
    open.setOnFinished( () => this.scheduleBlink( eyes ) );
    eyes.addTransform( open );
  }

  private startHeadSway( head: UIImage ): void
  {
    head.enableRotation();

    const amplitude = this.TIGER_HEAD_SWAY_DEG * Math.PI / 180;
    if ( !uiEffectsEnabled() )
    {
      head.setRotationImmediate( 0 );
      return;
    }
    head.setRotationImmediate( -amplitude );

    const sway = new TransformRotate(
      head, true, null, amplitude, performance.now(), this.TIGER_HEAD_SWAY_MS,
      { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true, infinite_loop: true }
    );
    head.addTransform( sway );
  }

  private buildBalloons( scroll_container: UIElement, tiger_x: number, tiger_y: number ): void
  {
    const string_w = LayoutScaler.widthFromDesign( this.BALLOON_STRING_WIDTH );
    const rim_y = this.BALLOON_STRING_RIM_Y;
    const knot_inset = 10;

    for ( let index = 0; index < this.BALLOONS.length; index++ )
    {
      const balloon = this.BALLOONS[index];
      const center_x = this.BALLOON_CLUSTER_CENTER_X + balloon.dx;
      const center_y = balloon.dy;
      const knot_y = center_y + balloon.h / 2 - knot_inset;

      const string_len = Math.max( 0, rim_y - knot_y );
      const string_h = LayoutScaler.heightFromDesign( string_len );
      const string_x = tiger_x + LayoutScaler.xFromDesign( center_x ) - string_w / 2;
      const string_y = tiger_y + LayoutScaler.yFromDesign( knot_y );

      const string_el = new UICanvasRect( this.ctx, string_x, string_y, string_w, string_h, ColorScheme.white, string_w / 2 );
      scroll_container.addChild( string_el );

      const balloon_w = LayoutScaler.widthFromDesign( balloon.w );
      const balloon_h = LayoutScaler.heightFromDesign( balloon.h );
      const balloon_x = tiger_x + LayoutScaler.xFromDesign( center_x ) - balloon_w / 2;
      const balloon_y = tiger_y + LayoutScaler.yFromDesign( center_y ) - balloon_h / 2;

      const balloon_img = new UIImage( this.ctx, balloon_x, balloon_y, balloon_w, balloon_h, balloon.img_id );
      scroll_container.addChild( balloon_img );

      const sway_amplitude = LayoutScaler.xFromDesign( this.BALLOON_SWAY_MIN_AMPLITUDE + Math.random() * ( this.BALLOON_SWAY_MAX_AMPLITUDE - this.BALLOON_SWAY_MIN_AMPLITUDE ) );
      const sway_duration = this.BALLOON_SWAY_MIN_MS + Math.random() * ( this.BALLOON_SWAY_MAX_MS - this.BALLOON_SWAY_MIN_MS );
      const sway_start = performance.now() - Math.random() * sway_duration;

      this.startSideSway( string_el, string_x, string_y, sway_amplitude, sway_duration, sway_start );
      this.startSideSway( balloon_img, balloon_x, balloon_y, sway_amplitude, sway_duration, sway_start );
    }
  }

  private startSideSway( target: UIElement, base_x: number, y: number, amplitude: number, duration_ms: number, start_time: number ): void
  {
    if ( !uiEffectsEnabled() )
    {
      target.setPixelPos( new Vector2( base_x, y ), true );
      return;
    }
    target.setPixelPos( new Vector2( base_x - amplitude, y ), true );

    const sway = new TransformStraightMove(
      target, true, null, new Vector2( base_x + amplitude, y ), start_time, duration_ms,
      { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true, infinite_loop: true }
    );
    target.addTransform( sway );
  }
}
